import { AppThunkAction } from '../reducers';

// services
import * as Services from '../../services/ContractStageItem.Services';

// actions
import * as Actions from '../actions';
import * as ActionEvents from '../actions/ContractStageItem.Actions';

// models
import { ErrorType } from '../../models/HttpError';
import { ContractStageItemViewModel } from '../../models/Entities/Contracts/ContractStageItemViewModel';
import {
    CreateContractStageItemCommand,
    UpdateContractStageItemCommand
} from '../../services/ContractStageItem.Services';

type KnownAction =
    | Actions.ContractStageItems.ContractStageItemGetAllRequestAction
    | Actions.ContractStageItems.ContractStageItemGetAllSuccessAction
    | Actions.ContractStageItems.ContractStageItemGetAllFailureAction
    | Actions.ContractStageItems.ContractStageItemGetByIdRequestAction
    | Actions.ContractStageItems.ContractStageItemGetByIdSuccessAction
    | Actions.ContractStageItems.ContractStageItemGetByIdFailureAction
    | Actions.ContractStageItems.ContractStageItemCreateRequestAction
    | Actions.ContractStageItems.ContractStageItemCreateSuccessAction
    | Actions.ContractStageItems.ContractStageItemCreateFailureAction
    | Actions.ContractStageItems.ContractStageItemUpdateRequestAction
    | Actions.ContractStageItems.ContractStageItemUpdateSuccessAction
    | Actions.ContractStageItems.ContractStageItemUpdateFailureAction
    | Actions.ContractStageItems.ContractStageItemDeleteRequestAction
    | Actions.ContractStageItems.ContractStageItemDeleteSuccessAction
    | Actions.ContractStageItems.ContractStageItemDeleteFailureAction;

export const actionCreators = {
    GetAllContractStageItems: (contractStageId: number): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (token === null) {
            dispatch({
                type: ActionEvents.CONTRACTSTAGEITEM_GET_ALL_FAILURE,
                contractStageId,
                error: {
                    ErrorCode: 401,
                    ErrorMessage: 'Not authorized',
                    Errors: []
                }
            });
            return;
        }

        dispatch({
            type: ActionEvents.CONTRACTSTAGEITEM_GET_ALL_REQUEST,
            contractStageId
        });

        Services.GetContractStageItems(token, contractStageId)
            .then((response: ContractStageItemViewModel[]) => {
                dispatch({
                    type: ActionEvents.CONTRACTSTAGEITEM_GET_ALL_SUCCESS,
                    contractStageId,
                    payload: response
                });
            })
            .catch((error: ErrorType) => {
                dispatch({
                    type: ActionEvents.CONTRACTSTAGEITEM_GET_ALL_FAILURE,
                    contractStageId,
                    error
                });
            });
    },

    GetContractStageItemById: (contractStageId: number, stageItemId: number): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (token === null) {
            dispatch({
                type: ActionEvents.CONTRACTSTAGEITEM_GET_BY_ID_FAILURE,
                contractStageId,
                stageItemId,
                error: {
                    ErrorCode: 401,
                    ErrorMessage: 'Not authorized',
                    Errors: []
                }
            });
            return;
        }

        dispatch({
            type: ActionEvents.CONTRACTSTAGEITEM_GET_BY_ID_REQUEST,
            contractStageId,
            stageItemId
        });

        Services.GetContractStageItemById(token, contractStageId, stageItemId)
            .then((response: ContractStageItemViewModel) => {
                dispatch({
                    type: ActionEvents.CONTRACTSTAGEITEM_GET_BY_ID_SUCCESS,
                    contractStageId,
                    payload: response
                });
            })
            .catch((error: ErrorType) => {
                dispatch({
                    type: ActionEvents.CONTRACTSTAGEITEM_GET_BY_ID_FAILURE,
                    contractStageId,
                    stageItemId,
                    error
                });
            });
    },

    AddContractStageItem: (
        contractStageId: number,
        payload: CreateContractStageItemCommand
    ): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (token === null) {
            dispatch({
                type: ActionEvents.CONTRACTSTAGEITEM_CREATE_FAILURE,
                contractStageId,
                error: {
                    ErrorCode: 401,
                    ErrorMessage: 'Not authorized',
                    Errors: []
                }
            });
            return;
        }

        dispatch({
            type: ActionEvents.CONTRACTSTAGEITEM_CREATE_REQUEST,
            contractStageId,
            payload
        });

        Services.AddContractStageItem(token, contractStageId, payload)
            .then((response: ContractStageItemViewModel) => {
                dispatch({
                    type: ActionEvents.CONTRACTSTAGEITEM_CREATE_SUCCESS,
                    contractStageId,
                    payload: response
                });
            })
            .catch((error: ErrorType) => {
                dispatch({
                    type: ActionEvents.CONTRACTSTAGEITEM_CREATE_FAILURE,
                    contractStageId,
                    error
                });
            });
    },

    UpdateContractStageItem: (
        contractStageId: number,
        stageItemId: number,
        payload: UpdateContractStageItemCommand
    ): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (token === null) {
            dispatch({
                type: ActionEvents.CONTRACTSTAGEITEM_UPDATE_FAILURE,
                contractStageId,
                stageItemId,
                error: {
                    ErrorCode: 401,
                    ErrorMessage: 'Not authorized',
                    Errors: []
                }
            });
            return;
        }

        dispatch({
            type: ActionEvents.CONTRACTSTAGEITEM_UPDATE_REQUEST,
            contractStageId,
            stageItemId,
            payload
        });

        Services.UpdateContractStageItem(token, contractStageId, stageItemId, payload)
            .then((response: ContractStageItemViewModel) => {
                dispatch({
                    type: ActionEvents.CONTRACTSTAGEITEM_UPDATE_SUCCESS,
                    contractStageId,
                    payload: response
                });
            })
            .catch((error: ErrorType) => {
                dispatch({
                    type: ActionEvents.CONTRACTSTAGEITEM_UPDATE_FAILURE,
                    contractStageId,
                    stageItemId,
                    error
                });
            });
    },

    DeleteContractStageItem: (
        contractStageId: number,
        stageItemId: number
    ): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (token === null) {
            dispatch({
                type: ActionEvents.CONTRACTSTAGEITEM_DELETE_FAILURE,
                contractStageId,
                stageItemId,
                error: {
                    ErrorCode: 401,
                    ErrorMessage: 'Not authorized',
                    Errors: []
                }
            });
            return;
        }

        dispatch({
            type: ActionEvents.CONTRACTSTAGEITEM_DELETE_REQUEST,
            contractStageId,
            stageItemId
        });

        Services.DeleteContractStageItem(token, contractStageId, stageItemId)
            .then(() => {
                dispatch({
                    type: ActionEvents.CONTRACTSTAGEITEM_DELETE_SUCCESS,
                    contractStageId,
                    stageItemId
                });
            })
            .catch((error: ErrorType) => {
                dispatch({
                    type: ActionEvents.CONTRACTSTAGEITEM_DELETE_FAILURE,
                    contractStageId,
                    stageItemId,
                    error
                });
            });
    }
};
